import type {
  AuthMe,
  FileResponse,
  GitStatus,
  GraphData,
  HistoryResponse,
  LinksResponse,
  NewToken,
  RevResponse,
  SearchResponse,
  Settings,
  Stats,
  TagCount,
  TokenInfo,
  TreeResponse,
  WriteResponse,
} from './types.js';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

type Json = Record<string, unknown> | unknown[];

async function toError(res: Response): Promise<ApiError> {
  let code = `http-${res.status}`;
  let message = res.statusText || `request failed with ${res.status}`;
  try {
    const body = (await res.json()) as { error?: unknown; message?: unknown };
    if (typeof body.error === 'string') code = body.error;
    if (typeof body.message === 'string') message = body.message;
  } catch {
    /* non-JSON error body: keep the status text */
  }
  return new ApiError(res.status, code, message);
}

async function call<T>(method: string, path: string, body?: Json | string | Uint8Array<ArrayBuffer>): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (body !== undefined) {
    if (typeof body === 'string') {
      (init.headers as Record<string, string>)['Content-Type'] = 'text/plain; charset=utf-8';
      init.body = body;
    } else if (body instanceof Uint8Array) {
      (init.headers as Record<string, string>)['Content-Type'] = 'application/octet-stream';
      init.body = body;
    } else {
      (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
  }
  const res = await fetch(path, init);
  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const q = (params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
};

export interface GitConfigInput {
  enabled: boolean;
  remote: string;
  branch: string;
  autoPush: boolean;
  pullIntervalMin: number;
  token?: string;
  authorName?: string;
  authorEmail?: string;
}

export interface SettingsInput {
  vaultName?: string;
  ignore?: string[];
  maxBlobBytes?: number;
  historyRetentionDays?: number;
  autoCommit?: boolean;
}

/** Thin typed wrapper over the `/api` surface described in PROTOCOL.md §5. */
export class Api {
  me(): Promise<AuthMe> {
    return call('GET', '/api/auth/me');
  }

  setup(setupToken: string, username: string, password: string): Promise<{ user: string }> {
    return call('POST', '/api/auth/setup', { setupToken, username, password });
  }

  login(username: string, password: string): Promise<{ user: string }> {
    return call('POST', '/api/auth/login', { username, password });
  }

  logout(): Promise<void> {
    return call('POST', '/api/auth/logout', {});
  }

  changePassword(current: string, next: string): Promise<{ ok: boolean }> {
    return call('POST', '/api/auth/password', { current, next });
  }

  tree(): Promise<TreeResponse> {
    return call('GET', '/api/fs/tree');
  }

  readFile(path: string): Promise<FileResponse> {
    return call('GET', `/api/fs/file${q({ path })}`);
  }

  writeFile(path: string, content: string, baseHash: string | null): Promise<WriteResponse> {
    return call('PUT', '/api/fs/file', { path, content, baseHash });
  }

  mkdir(path: string): Promise<void> {
    return call('POST', '/api/fs/mkdir', { path });
  }

  rename(from: string, to: string): Promise<RevResponse> {
    return call('POST', '/api/fs/rename', { from, to });
  }

  delete(path: string): Promise<RevResponse> {
    return call('POST', '/api/fs/delete', { path });
  }

  upload(path: string, data: Uint8Array<ArrayBuffer>): Promise<WriteResponse> {
    return call('POST', `/api/fs/upload${q({ path })}`, data);
  }

  /** Raw URL of a vault file, usable as an `<img src>` / download link. */
  rawUrl(path: string, download = false): string {
    return `/api/fs/raw${q({ path, download: download ? '1' : undefined })}`;
  }

  search(query: string, limit = 50): Promise<SearchResponse> {
    return call('GET', `/api/search${q({ q: query, limit })}`);
  }

  links(path: string): Promise<LinksResponse> {
    return call('GET', `/api/links${q({ path })}`);
  }

  graph(): Promise<GraphData> {
    return call('GET', '/api/graph');
  }

  tags(): Promise<{ tags: TagCount[] }> {
    return call('GET', '/api/tags');
  }

  history(path: string, limit = 100): Promise<HistoryResponse> {
    return call('GET', `/api/history${q({ path, limit })}`);
  }

  historyBlob(hash: string): Promise<Uint8Array<ArrayBuffer>> {
    return fetch(`/api/history/blob/${encodeURIComponent(hash)}`, { credentials: 'same-origin' }).then(async (res) => {
      if (!res.ok) throw await toError(res);
      return new Uint8Array(await res.arrayBuffer());
    });
  }

  restore(path: string, hash: string): Promise<RevResponse> {
    return call('POST', '/api/history/restore', { path, hash });
  }

  stats(): Promise<Stats> {
    return call('GET', '/api/stats');
  }

  settings(): Promise<Settings> {
    return call('GET', '/api/settings');
  }

  saveSettings(input: SettingsInput): Promise<{ ok: boolean; ignore: string[] }> {
    return call('PUT', '/api/settings', input as unknown as Json);
  }

  tokens(): Promise<{ tokens: TokenInfo[] }> {
    return call('GET', '/api/tokens');
  }

  createToken(label: string): Promise<NewToken> {
    return call('POST', '/api/tokens', { label });
  }

  revokeToken(id: string): Promise<void> {
    return call('DELETE', `/api/tokens/${encodeURIComponent(id)}`);
  }

  gitStatus(): Promise<GitStatus> {
    return call('GET', '/api/git/status');
  }

  saveGitConfig(input: GitConfigInput): Promise<GitStatus> {
    return call('PUT', '/api/git/config', input as unknown as Json);
  }

  gitAction(action: 'init' | 'commit' | 'push' | 'pull', message?: string): Promise<GitStatus> {
    if (action === 'commit') return call('POST', '/api/git/commit', { message: message ?? '' });
    return call('POST', `/api/git/${action}`, {});
  }
}

export const api = new Api();
